import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, type Theme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import { legacyTheme } from '../theme/legacyTheme';
import type { ThemeMode } from '../theme/tokens';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
}

export const ThemeContext = createContext<ThemeContextType>({
  mode: 'light',
  toggleTheme: () => {},
});

interface ThemeProviderProps {
  children: ReactNode;
  /**
   * Builds the MUI theme for a mode. The customer entry (src/index.tsx) passes the customer theme;
   * the management console's entry passes nothing and keeps the theme it has always had.
   */
  buildTheme?: (mode: ThemeMode) => Theme;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, buildTheme = legacyTheme }) => {
  // Get theme preference from localStorage or default to 'light'
  const [mode, setMode] = useState<ThemeMode>(() => {
    const savedMode = localStorage.getItem('themeMode');
    return (savedMode === 'dark' ? 'dark' : 'light') as ThemeMode;
  });
  
  // Generate the theme based on the current mode
  const theme = React.useMemo(() => buildTheme(mode), [buildTheme, mode]);

  // Toggle between light and dark mode
  const toggleTheme = () => {
    setMode((prevMode) => {
      const newMode = prevMode === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', newMode);
      return newMode;
    });
  };
  
  // Apply the theme class to the body element
  useEffect(() => {
    document.body.classList.remove('light-mode', 'dark-mode');
    document.body.classList.add(`${mode}-mode`);
  }, [mode]);
  
  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};